import { Divider, FormGroup, FormParagraph, FormSection, Radio, RadioGroup } from '@ksyos/design-system';
import { isBoolean } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { Profession } from '@common/enums/profession';
import { SendSleepRecorderOption, SleepRecorderType } from '@common/validation/customControls/somnology';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';

interface ISelectSleepRecorderProps {
    canReferPatientWithoutSmartphone: boolean;
    errors?: ValidationErrors;
    hasErrors: boolean;
    onChange: (sleepRecorderDetails: {
        distributionMethod: SendSleepRecorderOption | null;
        patientHasSmartphone: boolean | null;
        sleepRecorderType: SleepRecorderType | null;
    }) => void | Promise<void>;
    values: {
        distributionMethod: SendSleepRecorderOption | null;
        patientHasSmartphone: boolean | null;
        sleepRecorderType: SleepRecorderType | null;
    };
}

export function SelectSleepRecorder(props: Readonly<ISelectSleepRecorderProps>) {
    const belongsToKsyosSlaapOrganization = useSelector((state: IReduxState) =>
        [Profession.outpatientAssistant, Profession.osasNurse].includes(state.authentication.profession)
    );
    const [patientHasSmartphone, setPatientHasSmartphone] = useState<boolean | null>(props.values.patientHasSmartphone);

    const isNightOwlMiniOutOfStock = useFrontendSettings().frontendSettings.isNightOwlMiniOutOfStock;

    const translate = getSelectedLanguage();

    useEffect(() => {
        if (
            !props.canReferPatientWithoutSmartphone &&
            !belongsToKsyosSlaapOrganization &&
            !props.values.distributionMethod
        ) {
            props.onChange({
                distributionMethod: SendSleepRecorderOption.sendByKsyos,
                patientHasSmartphone: null,
                sleepRecorderType: isNightOwlMiniOutOfStock
                    ? SleepRecorderType.watchpatOne
                    : SleepRecorderType.nightowl,
            });
        }
    }, []);

    useEffect(() => {
        if (patientHasSmartphone !== props.values.patientHasSmartphone) {
            handleChange({
                belongsToKsyosSlaapOrganization,
                canReferPatientWithoutSmartphone: props.canReferPatientWithoutSmartphone,
                isNightOwlMiniOutOfStock,
                onChange: props.onChange,
                patientHasSmartphone,
            });
        }
    }, [patientHasSmartphone]);

    return (
        <>
            <Divider />
            <FormSection title="Onderzoek met slaaprecorder">
                {(props.canReferPatientWithoutSmartphone || belongsToKsyosSlaapOrganization) && (
                    <FormGroup
                        hasErrors={
                            props.errors?.has('customControlData.typeSlaaprecorder') && !isBoolean(patientHasSmartphone)
                        }
                        isRequired={true}
                        label={translate.osas.patientHasSmartphone}
                        tooltipText="Heeft de patiënt geen smartphone, wil of kan de patiënt de app niet installeren of gebruiken, of is de patiënt niet technisch onderlegt? Kies dan Nee."
                    >
                        <RadioGroup
                            error={getValidationErrorTranslation(
                                !isBoolean(patientHasSmartphone)
                                    ? props.errors?.get('customControlData.typeSlaaprecorder')
                                    : ''
                            )}
                            name="patient-has-smartphone"
                            onChange={(event) => setPatientHasSmartphone(event.target.value === 'yes')}
                            value={patientHasSmartphone === null ? '' : patientHasSmartphone ? 'yes' : 'no'}
                        >
                            <Radio
                                label={translate.common.yes}
                                value="yes"
                            ></Radio>
                            <Radio
                                label={translate.common.no}
                                value="no"
                            ></Radio>
                        </RadioGroup>
                    </FormGroup>
                )}
                {belongsToKsyosSlaapOrganization && isBoolean(patientHasSmartphone) && (
                    <FormGroup label={translate.osas.sleepRecorderType}>
                        <FormParagraph>
                            {patientHasSmartphone ? SleepRecorderType.watchpatOne : SleepRecorderType.watchpat300}
                        </FormParagraph>
                    </FormGroup>
                )}
                {!belongsToKsyosSlaapOrganization && (
                    <FormGroup label={translate.osas.sendSleepRecorder}>
                        <FormParagraph>{translate.osas[SendSleepRecorderOption.sendByKsyos]}</FormParagraph>
                    </FormGroup>
                )}
            </FormSection>
            <Divider />
        </>
    );
}

async function handleChange({
    belongsToKsyosSlaapOrganization,
    canReferPatientWithoutSmartphone,
    isNightOwlMiniOutOfStock,
    onChange,
    patientHasSmartphone,
}: {
    belongsToKsyosSlaapOrganization: boolean;
    canReferPatientWithoutSmartphone: ISelectSleepRecorderProps['canReferPatientWithoutSmartphone'];
    isNightOwlMiniOutOfStock: boolean;
    onChange: ISelectSleepRecorderProps['onChange'];
    patientHasSmartphone: ISelectSleepRecorderProps['values']['patientHasSmartphone'];
}) {
    if (!belongsToKsyosSlaapOrganization) {
        if (canReferPatientWithoutSmartphone) {
            if (patientHasSmartphone === true) {
                await onChange({
                    distributionMethod: SendSleepRecorderOption.sendByKsyos,
                    patientHasSmartphone,
                    sleepRecorderType: isNightOwlMiniOutOfStock
                        ? SleepRecorderType.watchpatOne
                        : SleepRecorderType.nightowl,
                });
            } else if (patientHasSmartphone === false) {
                await onChange({
                    distributionMethod: SendSleepRecorderOption.sendByKsyos,
                    patientHasSmartphone,
                    sleepRecorderType: SleepRecorderType.watchpat300,
                });
            }
        } else {
            await onChange({
                distributionMethod: SendSleepRecorderOption.sendByKsyos,
                patientHasSmartphone: null,
                sleepRecorderType: isNightOwlMiniOutOfStock
                    ? SleepRecorderType.watchpatOne
                    : SleepRecorderType.nightowl,
            });
        }
    } else if (isBoolean(patientHasSmartphone)) {
        await onChange({
            distributionMethod: SendSleepRecorderOption.sendByKsyos,
            patientHasSmartphone,
            sleepRecorderType: patientHasSmartphone ? SleepRecorderType.watchpatOne : SleepRecorderType.watchpat300,
        });
    }
}
